import React, { useState, useEffect } from 'react';
import { Sidebar } from '../../components/moderator/Sidebar';
import { Navbar } from '../Navbar';
import config from '../../config/config';
import { useNavigate, useParams } from "react-router-dom";

export default function EditService() {
  const userId = localStorage.userId;
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [formData, setFormData] = useState({
    typeOfAnimal: '',
    services: '',
    day: '',
    firstName: '',
    lastName: '',
    contact: '',
    address: '',
    agreedAmount: '',
    amountPaid: '',
    amountDue: '0'
  });

  const serviceOptions = [
    'Slaughtering',
    'Slaughtering and Caretaking',
    'Caretaking',
    'Caretaking with Feeding',
    'Slaughtering and Caretaking with Feeding'
  ];

  const dayOptions = ['Day 1', 'Day 2', 'Day 3'];

  // Fetch existing data
  useEffect(() => {
    const fetchServiceData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${config.baseUrl}/api/services/getServiceById/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log(response);
        if (!response.ok) throw new Error('Failed to fetch data');
        const data = await response.json();
        
        // Map backend keys to form state keys
        setFormData({
          typeOfAnimal: data.type_of_animal || '',
          services: data.service_type || '',
          day: data.day || '',
          firstName: data.first_name || '',
          lastName: data.last_name || '',
          contact: data.contact || '',
          address: data.address || '',
          agreedAmount: data.agreed_amount || '',
          amountPaid: data.amount_paid || '',
          amountDue: data.amount_due || '0'
        });
      } catch (error) {
        console.error(error);
        alert("Error loading service details");
      } finally {
        setFetching(false);
      }
    };
    fetchServiceData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updatedData = { ...prev, [name]: value };
      if (name === 'agreedAmount' || name === 'amountPaid') {
        const agreed = name === 'agreedAmount' ? Number(value) : Number(prev.agreedAmount);
        const paid = name === 'amountPaid' ? Number(value) : Number(prev.amountPaid);
        updatedData.amountDue = (agreed - paid).toString();
      }
      return updatedData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.baseUrl}/api/services/editOthService/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({...formData,userId})
      });

      if (response.ok) {
        alert('Updated successfully');
        navigate('/othService/view');
      } 
      else {
        const errorData = await response.json();
        alert(errorData.error || "Failed to update service");
      }
    } catch (error) {
      alert('Network error. Failed to update.');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
    </div>
  );

  return (
      <main className="">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Edit Service</h1>
              <p className="text-sm text-gray-500">Update details for Reference #{id}</p>
            </div>
            <button onClick={() => navigate(-1)} className="text-sm font-bold text-green-600 hover:underline">
              ← Back to List
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <form onSubmit={handleSubmit} className="p-8 space-y-8">
              {/* Row 1: Animal & Timing */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">Type of Animal</label>
                  <select
                    name="typeOfAnimal"
                    value={formData.typeOfAnimal}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-50 outline-none transition-all"
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="cow">Cow</option>
                    <option value="goat">Goat</option>
                    <option value="sheep">Sheep</option>
                    <option value="camel">Camel</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">Select Day</label>
                  <select
                    name="day"
                    value={formData.day}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-50 outline-none transition-all"
                    required
                  >
                    <option value="">Select Day</option>
                    {dayOptions.map(day => <option key={day} value={day}>{day}</option>)}
                  </select>
                </div>

                <div className="md:col-span-2 space-y-2">
                  <label className="text-sm font-bold text-gray-700">Service Required</label>
                  <select
                    name="services"
                    value={formData.services}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-50 outline-none transition-all"
                    required
                  >
                    <option value="">Select Services</option>
                    {serviceOptions.map(service => <option key={service} value={service}>{service}</option>)}
                  </select>
                </div>
              </div>

              <hr className="border-gray-100" />

              {/* Row 2: Customer Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">First Name</label>
                  <input
                    type="text" name="firstName" value={formData.firstName} onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-50 outline-none"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">Last Name</label>
                  <input
                    type="text" name="lastName" value={formData.lastName} onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-50 outline-none"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">Contact Number</label>
                  <input
                    type="tel" name="contact" value={formData.contact} onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-50 outline-none"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">Address</label>
                  <textarea
                    name="address" value={formData.address} onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-50 outline-none"
                    rows="1" required
                  />
                </div>
              </div>

              <hr className="border-gray-100" />

              {/* Row 3: Payment */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">Agreed Amount</label>
                  <input
                    type="number" name="agreedAmount" value={formData.agreedAmount} onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-50 outline-none"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">Amount Paid</label>
                  <input
                    type="number" name="amountPaid" value={formData.amountPaid} onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:border-green-500 focus:ring-2 focus:ring-green-50 outline-none"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-gray-700">Remaining Balance</label>
                  <input
                    type="number" value={formData.amountDue} readOnly
                    className="w-full border border-transparent rounded-lg p-3 text-sm bg-red-50 text-red-700 font-bold cursor-not-allowed outline-none"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-6 py-2.5 rounded-lg text-sm font-bold text-gray-500 hover:bg-gray-100 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`bg-indigo-800 text-white px-10 py-2.5 rounded-lg text-sm font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-green-100 ${loading ? 'opacity-50' : ''}`}
                >
                  {loading ? 'Updating...' : 'Update Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
  );
}